import {
    Box, Button, Dialog, DialogActions, DialogContent,
    DialogTitle, Paper, TextField, Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BusinessIcon from "@mui/icons-material/Business";
import AddIcon from "@mui/icons-material/Add";
import GroupAddIcon from "@mui/icons-material/GroupAdd";

import {
    fetchOrganizations,
    createOrganization,
    joinOrganization,
} from "../api/organizations";
import OrganizationModal from "../components/Organizations/OrganizationModal";

export default function Organizations({ translate, farms }) {
    const navigate = useNavigate();

    const [organizations, setOrganizations] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [joinOpen, setJoinOpen] = useState(false);
    const [organizationName, setOrganizationName] = useState("");
    const [inviteCode, setInviteCode] = useState("");
    const [loading, setLoading] = useState(false);
    
    const normalizeOrganizations = (response) => {
        if (!response) return [];
        if (Array.isArray(response?.[0])) return response[0];
        if (Array.isArray(response)) return response;
        return [];
    };

    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [organizationModalOpen, setOrganizationModalOpen] = useState(false);
    
    const extractOrganization = (response) => {
        if (!response) return null;
        if (Array.isArray(response?.[0])) return response[0][0];
        if (Array.isArray(response)) return response[0];
        return response;
    };

    const loadOrganizations = async () => {
        try {
            const response = await fetchOrganizations();
            setOrganizations(normalizeOrganizations(response));
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        loadOrganizations();
    }, []);

    const handleCreate = async () => {
        if (!organizationName.trim()) return;

        try {
            setLoading(true);

            const response = await createOrganization(
                organizationName.trim()
            );

            const organization =
                extractOrganization(response);

            if (organization) {
                setOrganizations(prev => [
                    organization,
                    ...prev,
                ]);
            }

            setCreateOpen(false);
            setOrganizationName("");
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleJoin = async () => {
        if (!inviteCode.trim()) return;

        try {
            setLoading(true);

            const response = await joinOrganization(
                inviteCode.trim().toUpperCase()
            );

            const organization =
                extractOrganization(response);

            if (organization) {
                setOrganizations(prev => [
                    organization,
                    ...prev,
                ]);
            }

            setJoinOpen(false);
            setInviteCode("");
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 4,
                    p: { xs: 2, sm: 3 },
                    border: "1px solid rgba(0,0,0,0.08)",
                    backgroundColor: "var(--color-bg-primary)",
                    minHeight: 400,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}>
                        <BusinessIcon sx={{
                            color: "var(--color-green-main)",
                        }} />

                        <Typography sx={{
                            fontFamily: "Fraunces",
                            fontSize: { xs: 22, sm: 26 },
                            color: "var(--color-text-primary)",
                        }}>
                            {translate("organizations")}
                        </Typography>
                    </Box>

                    <Box sx={{
                        display: "flex",
                        gap: 1,
                        width: { xs: "100%", sm: "auto" },
                    }}>
                        <Button
                            variant="outlined"
                            startIcon={<GroupAddIcon />}
                            onClick={() => setJoinOpen(true)}
                            sx={{
                                borderRadius: 3,
                                textTransform: "none",
                                flexGrow: { xs: 1, sm: 0 },
                                fontSize: { xs: "0.85rem", sm: "0.875rem" },
                                py: { xs: 1, sm: 0.75 }
                            }}
                        >
                            {translate("join")}
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setCreateOpen(true)}
                            sx={{
                                backgroundColor: "var(--color-green-main)",
                                color: "var(--color-text-inverse)",
                                borderRadius: 3,
                                textTransform: "none",
                                flexGrow: { xs: 1, sm: 0 },
                                fontSize: { xs: "0.85rem", sm: "0.875rem" },
                                py: { xs: 1, sm: 0.75 },
                                "&:hover": {
                                    backgroundColor: "var(--color-green-strong)",
                                },
                            }}
                        >
                            {translate("create")}
                        </Button>
                    </Box>
                </Box>

                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    overflowY: "auto",
                }}>
                    {!organizations.length && (
                        <Typography sx={{
                            textAlign: "center",
                            color: "text.secondary",
                            py: 6,
                        }}>
                            {translate("no_organizations_yet")}
                        </Typography>
                    )}

                    {organizations.map((organization) => (
                        <Paper
                            key={organization.organization_id}
                            elevation={0}
                            onClick={() => {
                                setSelectedOrganization(organization);
                                setOrganizationModalOpen(true);
                            }}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                backgroundColor: "var(--color-green-soft)",
                                border: "1px solid var(--color-green-strong)",
                                cursor: "pointer",
                                transition: "0.2s ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                                },
                            }}
                        >
                            <Typography sx={{
                                fontWeight: 700,
                                color: "var(--color-text-primary)",
                            }}>
                                {organization.organization_name}
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{ color: "text.secondary" }}
                            >
                                {translate(organization.role)}
                            </Typography>
                        </Paper>
                    ))}
                </Box>
            </Paper>

            <Dialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {translate("create_organization")}
                </DialogTitle>

                <DialogContent>
                    <TextField
                        fullWidth
                        autoFocus
                        margin="dense"
                        label={translate("organization_name")}
                        value={organizationName}
                        onChange={(e) =>
                            setOrganizationName(e.target.value)
                        }
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>
                        {translate("cancel")}
                    </Button>

                    <Button
                        variant="contained"
                        disabled={loading}
                        onClick={handleCreate}
                    >
                        {translate("create")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={joinOpen}
                onClose={() => setJoinOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {translate("join_organization")}
                </DialogTitle>

                <DialogContent>
                    <TextField
                        fullWidth
                        autoFocus
                        margin="dense"
                        label={translate("invite_code")}
                        value={inviteCode}
                        onChange={(e) =>
                            setInviteCode(
                                e.target.value.toUpperCase()
                            )
                        }
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setJoinOpen(false)}>
                        {translate("cancel")}
                    </Button>

                    <Button
                        variant="contained"
                        disabled={loading}
                        onClick={handleJoin}
                    >
                        {translate("join")}
                    </Button>
                </DialogActions>
            </Dialog>

            <OrganizationModal
                open={organizationModalOpen}
                onClose={() => {
                    setOrganizationModalOpen(false);
                    setSelectedOrganization(null);
                }}
                organization={selectedOrganization}
                farms={farms}
                translate={translate}
                onRefresh={loadOrganizations}
            />
        </>
    );
}